import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

const NAV_LINKS = [
  { label: "Home", to: "/" },
  { label: "Capabilities", to: "/capabilities" },
  { label: "Sectors", to: "/sectors" },
  { label: "Featured Products & Services", to: "/featured" },
  { label: "Leadership", to: "/leadership" },
];

export default function TopNav() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const isActive = (path) => {
    if (path === "/") return location.pathname === "/";
    return location.pathname.startsWith(path);
  };

  return (
    <header
      className={`sticky top-0 z-50 bg-white/95 backdrop-blur-sm border-b border-border transition-shadow duration-500 ${
        scrolled ? "nav-scrolled" : ""
      }`}
      data-testid="top-nav"
    >
      <div className="max-w-[1240px] mx-auto px-5 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 lg:h-[72px] relative">
          {/* Logo */}
          <Link
            to="/"
            className="flex items-center gap-3 shrink-0 group"
            data-testid="top-nav-logo"
          >
            <motion.img
              src="https://customer-assets.emergentagent.com/job_cortes-execution/artifacts/im0ztelm_cortes_logo_transparent.png"
              alt="Cortes Industries"
              className="w-9 h-9 object-contain"
              whileHover={{ scale: 1.05 }}
              transition={{ duration: 0.2 }}
            />
            <div className="hidden sm:block">
              <span className="text-2xl tracking-tight text-foreground uppercase" style={{ fontFamily: "'Cortes Industrial', 'Black Ops One', sans-serif", letterSpacing: '0.02em' }}>
                Cortes Industries
              </span>
            </div>
          </Link>

          {/* Mobile Centered Brand Name */}
          <div className="sm:hidden absolute left-1/2 -translate-x-1/2">
            <span className="text-lg text-foreground uppercase" style={{ fontFamily: "'Cortes Industrial', 'Black Ops One', sans-serif", letterSpacing: '0.04em' }}>
              Cortes Industries
            </span>
          </div>

          {/* Desktop Nav */}
          <nav className="hidden lg:flex items-center gap-1">
            {NAV_LINKS.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                data-testid={`top-nav-link-${link.to.replace('/', '') || 'home'}`}
                className={`relative px-3 py-2 text-sm font-medium transition-colors rounded-md ${
                  isActive(link.to)
                    ? "text-foreground"
                    : "text-muted-foreground hover:text-foreground"
                }`}
              >
                {link.label}
                {isActive(link.to) && (
                  <motion.div
                    layoutId="nav-indicator"
                    className="absolute bottom-0 left-3 right-3 h-[2px] bg-[hsl(41,47%,61%)]"
                    transition={{ type: "spring", bounce: 0.2, duration: 0.5 }}
                  />
                )}
              </Link>
            ))}
          </nav>

          {/* Desktop CTA */}
          <Link
            to="/contact"
            data-testid="top-nav-contact-link"
            className="hidden lg:inline-flex items-center px-5 py-2.5 text-sm font-semibold bg-[hsl(218,60%,10%)] text-white rounded-md hover:bg-[hsl(218,60%,14%)] transition-all btn-press"
          >
            Contact
          </Link>

          {/* Mobile Toggle */}
          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            className="lg:hidden p-2 rounded-md hover:bg-secondary"
            data-testid="top-nav-mobile-toggle"
            aria-label="Toggle navigation"
          >
            {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>

        {/* Mobile Menu */}
        <AnimatePresence>
          {mobileOpen && (
            <motion.div
              className="lg:hidden border-t border-border pb-4 pt-2 overflow-hidden"
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.3, ease: [0.25, 0.1, 0.25, 1] }}
            >
              <nav className="flex flex-col gap-1">
                {NAV_LINKS.map((link, i) => (
                  <motion.div
                    key={link.to}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: i * 0.05 }}
                  >
                    <Link
                      to={link.to}
                      onClick={() => setMobileOpen(false)}
                      className={`px-3 py-2.5 text-sm font-medium rounded-md ${
                        isActive(link.to)
                          ? "text-foreground bg-secondary"
                          : "text-muted-foreground hover:text-foreground hover:bg-secondary/60"
                      }`}
                    >
                      {link.label}
                    </Link>
                  </motion.div>
                ))}
                <motion.div
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: NAV_LINKS.length * 0.05 }}
                >
                  <Link
                    to="/contact"
                    onClick={() => setMobileOpen(false)}
                    className="mt-2 mx-3 inline-flex items-center justify-center px-5 py-2.5 text-sm font-semibold bg-[hsl(218,60%,10%)] text-white rounded-md"
                  >
                    Contact
                  </Link>
                </motion.div>
              </nav>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </header>
  );
}
